import { REQUIRED_MESSAGE, requiredString } from '@wyz/utils';
import { z } from 'zod';
import { TranslationKeys } from '~/common/types';

export const S2S_FLOW_TYPES = [
  'INQUIRY',
  'TYRES_ORDER',
  'SEARCH_QUOTES',
] as const;
export const S2S_ACTIVE_STATUSES = ['ACTIVE', 'INACTIVE'] as const;

export const s2sFlowFormSchema = z.object({
  type: z.enum(S2S_FLOW_TYPES),
  partner: z.string().nullable().optional(),
  status: z.enum(S2S_ACTIVE_STATUSES),
  url: z.string().nullable().optional(),
});

export const s2sBusinessRuleFormEntrySchema = z.object({
  id: z.string(),
  parameters: z.record(z.string(), z.string()),
});

export const s2sFlowTemplateFormSchema = z.object({
  name: requiredString,
  criteria: z
    .array(
      z.object({
        criterion: z.object({ id: z.string() }),
        values: z.array(requiredString).min(1, { message: REQUIRED_MESSAGE }),
      }),
    )
    .min(1, { message: REQUIRED_MESSAGE }),
  flows: z.array(s2sFlowFormSchema),
  businessRules: z.array(s2sBusinessRuleFormEntrySchema),
  newFlowType: z.string().optional(),
  newFlowPartner: z.string().optional(),
});

const s2sBusinessRuleLabelSchema = z.object({
  defaultValue: z.string(),
  i18nKey: z.custom<keyof TranslationKeys>(),
});

const s2sBusinessRuleParameterDefinitionSchema = z.object({
  key: z.string(),
  i18nKey: z.custom<keyof TranslationKeys>(),
  type: z.string(),
  defaultValue: z.string().nullable(),
  constraints: z.array(
    z.object({
      type: z.string(),
      options: z.record(z.string(), z.string()),
    }),
  ),
});
export const s2sBusinessRuleSchema = z.object({
  id: z.string(),
  name: s2sBusinessRuleLabelSchema,
  description: s2sBusinessRuleLabelSchema,
  code: z.string(),
  parameterDefinitions: z.array(s2sBusinessRuleParameterDefinitionSchema),
});

const embeddedBusinessRuleSchema = z.object({
  rule: s2sBusinessRuleSchema,
  parameters: z.array(z.object({ key: z.string(), value: z.string() })),
});
const payloadEmbeddedBusinessRuleSchema = z.object({
  rule: z.object({ id: z.string() }),
  parameters: z.array(z.object({ key: z.string(), value: z.string() })),
});

export const s2sFlowTemplatePayloadSchema = s2sFlowTemplateFormSchema
  .omit({
    criteria: true,
    businessRules: true,
    newFlowType: true,
    newFlowPartner: true,
  })
  .extend({
    criteria: z.array(
      z.object({
        criterion: z.object({ id: z.string() }),
        values: z.array(
          z.object({
            id: z.string(),
            label: z.string(),
          }),
        ),
      }),
    ),
    businessRules: z.array(payloadEmbeddedBusinessRuleSchema),
  });

export const s2sFlowTemplateSchema = s2sFlowTemplatePayloadSchema
  .omit({
    businessRules: true,
  })
  .extend({
    id: z.string(),
    status: z.enum(S2S_ACTIVE_STATUSES),
    businessRules: z.array(embeddedBusinessRuleSchema),
  });
